import { useState } from 'react';

import { Button, Card, MonoLabel, Skeleton, Toggle } from '@/design-system';
import type { ReportLinkStatistics, ReportShare } from '@/entities/report';
import { formatDayLong, formatDayTimeShort } from '@/shared/lib';

import { copyText } from '../browser';
import { previewCopy } from '../copy';

export interface SharingBenchmark {
    /** Gemiddeld aantal mensen dat één link opent. */
    sharesPerLink: string;
    /** Aandeel kopers dat binnen een week een vervolgdienst aanvraagt. */
    followUpRate: string;
}

export type LinkSetting = 'costs' | 'forward' | 'code';

export interface AdminSidebarProps {
    /** `null` zolang de cijfers laden. */
    statistics: ReportLinkStatistics | null;
    /** De link waar de schakelaars over gaan; `null` als die er niet is of niet te beheren valt. */
    share: ReportShare | null;
    /** Mag deze persoon links beheren (`reports.share`)? */
    canManage: boolean;
    busy: boolean;
    error: string | null;
    /** Een net gemaakte toegangscode; staat alleen hier en maar één keer. */
    issuedCode: string | null;
    /** De code ging al per sms naar de ontvangers (opnieuw versturen met een nieuwe link). */
    issuedCodeSent?: boolean;
    onDismissCode: () => void;
    onToggle: (setting: LinkSetting, next: boolean) => void;
    onNewCode: () => void;
    onCreateLink: () => void;
    companyName: string;
    /** Het adres zoals het in het bericht aan de klant staat. */
    address: string;
    /** "Vragen? Bel Keurhuis Voorbeeld op 085 123 45 67." */
    contactLine: string | null;
    /**
     * Platformcijfers. Zonder cijfers toont de kaart alleen de zin - liever
     * niets dan een verzonnen percentage.
     */
    benchmark?: SharingBenchmark | null;
}

/**
 * Wat het bedrijf ziet, ziet de klant niet. Alles in deze kolom - plus de
 * donkere kop en het browserchroom - bestaat alleen in de preview.
 *
 * De schakelaars wijzigen de nieuwste link die niet is ingetrokken
 * (`PATCH /reports/{report}/shares/{share}`); de klant ziet het verschil bij
 * zijn volgende bezoek.
 */
export function AdminSidebar({
    statistics,
    share,
    canManage,
    busy,
    error,
    issuedCode,
    issuedCodeSent = false,
    onDismissCode,
    onToggle,
    onNewCode,
    onCreateLink,
    companyName,
    address,
    contactLine,
    benchmark = null,
}: AdminSidebarProps) {
    const linkState = share?.state ?? statistics?.linkState ?? 'none';
    const settings = {
        costs: share?.showCosts ?? statistics?.costsVisible ?? true,
        forward: share?.allowForward ?? statistics?.sharingEnabled ?? true,
        code: share?.accessCodeRequired ?? statistics?.accessCodeRequired ?? false,
    };
    const editable = canManage && share !== null && share.state !== 'revoked' && !busy;
    const link = statistics?.shortUrl ?? statistics?.url ?? share?.url ?? null;

    return (
        <aside className="flex min-w-[280px] flex-[0_1_314px] flex-col gap-[14px]">
            <Card padding="md">
                <MonoLabel size={10} className="mb-3 block">
                    {previewCopy.phoneTitle}
                </MonoLabel>
                <div className="rounded-[22px] border border-[var(--placeholder-border)] bg-[var(--surface-question-head)] p-[10px]">
                    <span
                        aria-hidden="true"
                        className="mx-auto mt-[2px] mb-[10px] block h-[5px] w-[42px] rounded-[3px] bg-[var(--placeholder-border)]"
                    />
                    <div className="rounded-[14px] border border-[var(--border-phone-mock)] bg-[var(--surface-card)] p-[13px]">
                        <div className="mb-[7px] text-[11px] leading-[1.3] text-[var(--text-muted-mono-safe)]">
                            {previewCopy.phoneSender(companyName)}
                        </div>
                        <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-primary)]">
                            {previewCopy.phoneMessage(address)}
                        </p>
                        {link ? (
                            <p className="mt-[7px] mb-0 text-[13px] leading-[1.4] break-all text-[var(--accent)]">
                                {link}
                            </p>
                        ) : null}
                        {contactLine ? (
                            <p className="mt-[10px] mb-0 text-[11px] leading-[1.45] text-[var(--text-muted-mono-safe)]">
                                {contactLine}
                            </p>
                        ) : null}
                    </div>
                    <div aria-hidden="true" className="mt-[10px] flex gap-[6px]">
                        <span className="h-[34px] flex-1 rounded-[17px] border border-[var(--border-phone-mock)] bg-[var(--surface-card)]" />
                        <span className="h-[34px] w-[34px] rounded-full bg-[var(--accent)]" />
                    </div>
                </div>
            </Card>

            {issuedCode ? (
                <IssuedCode code={issuedCode} sent={issuedCodeSent} onDismiss={onDismissCode} />
            ) : null}

            <Card padding="md">
                <h2 className="m-0 mb-3 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]">
                    {previewCopy.accessTitle}
                </h2>

                {statistics === null ? (
                    <Skeleton height={120} radius={8} label={previewCopy.accessTitle} />
                ) : (
                    <dl className="m-0 flex flex-col gap-[10px]">
                        {rowsFor(statistics, linkState).map(([label, value]) => (
                            <div
                                key={label}
                                className="flex justify-between gap-[10px] text-[13px]"
                            >
                                <dt className="text-[var(--text-tertiary-safe)]">{label}</dt>
                                <dd className="m-0 text-right text-[var(--text-primary)]">
                                    {value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                )}

                {linkState !== 'active' && statistics !== null ? (
                    <div className="mt-[14px] rounded-[8px] border border-[var(--warn-border)] bg-[var(--warn-tint)] p-3">
                        <p className="m-0 text-[13px] leading-[1.5] font-medium text-[var(--warn-ink)]">
                            {previewCopy.linkTitle[linkState]}
                        </p>
                        <p className="m-0 mt-1 text-[12px] leading-[1.5] text-[var(--warn-ink)]">
                            {previewCopy.linkBody[linkState]}
                        </p>
                        {canManage ? (
                            <Button
                                variant="outline"
                                size="md"
                                className="mt-3"
                                disabled={busy}
                                onClick={onCreateLink}
                            >
                                {previewCopy.createLink}
                            </Button>
                        ) : null}
                    </div>
                ) : null}

                <div className="my-[14px] h-px bg-[var(--border-subtle)]" />

                <Toggle
                    checked={settings.costs}
                    disabled={!editable}
                    onCheckedChange={(next) => onToggle('costs', next)}
                    label={previewCopy.toggleCosts}
                />
                <Toggle
                    checked={settings.forward}
                    disabled={!editable}
                    onCheckedChange={(next) => onToggle('forward', next)}
                    label={previewCopy.toggleForward}
                />
                <Toggle
                    checked={settings.code}
                    disabled={!editable}
                    onCheckedChange={(next) => onToggle('code', next)}
                    label={previewCopy.toggleCode}
                    {...(settings.code ? { description: previewCopy.toggleCodeOn } : {})}
                />
                {settings.code && editable ? (
                    <Button variant="outline" size="md" className="mt-2" onClick={onNewCode}>
                        {previewCopy.newCode}
                    </Button>
                ) : null}

                {error ? (
                    <p
                        role="alert"
                        className="m-0 mt-3 text-[13px] leading-[1.5] text-[var(--danger)]"
                    >
                        {error}
                    </p>
                ) : null}

                {statistics && statistics.events.length > 0 ? (
                    <>
                        <div className="my-[14px] h-px bg-[var(--border-subtle)]" />
                        <MonoLabel size={10} tone="muted" as="h3" className="m-0 mb-2 block">
                            {previewCopy.eventsTitle}
                        </MonoLabel>
                        <ul className="m-0 flex list-none flex-col gap-[6px] p-0">
                            {statistics.events.slice(0, 6).map((event) => (
                                <li
                                    key={`${event.type}:${event.occurredAt}:${event.recipient ?? ''}`}
                                    className="text-[12px] leading-[1.45] text-[var(--text-secondary)]"
                                >
                                    {previewCopy.eventLine(
                                        event.label,
                                        event.recipient,
                                        formatDayTimeShort(event.occurredAt),
                                    )}
                                    {event.message ? (
                                        <q className="mt-[2px] block text-[var(--text-primary)] break-words whitespace-pre-line">
                                            {event.message}
                                        </q>
                                    ) : null}
                                </li>
                            ))}
                        </ul>
                    </>
                ) : null}
            </Card>

            <Card variant="dark" padding="md">
                <MonoLabel size={10} tone="onDarkMuted" className="mb-[10px] block">
                    {previewCopy.whyTitle}
                </MonoLabel>
                <p className="m-0 text-[13px] leading-[1.55] text-[var(--text-on-dark-2)]">
                    {previewCopy.whyBody}
                </p>
                {benchmark ? (
                    <div className="mt-[14px] flex gap-[18px]">
                        <Figure
                            value={benchmark.sharesPerLink}
                            caption={previewCopy.benchmarkShares}
                        />
                        <Figure
                            value={benchmark.followUpRate}
                            caption={previewCopy.benchmarkFollowUp}
                        />
                    </div>
                ) : null}
            </Card>
        </aside>
    );
}

/** De nieuwe code, één keer. Wegklikken is definitief. */
function IssuedCode({
    code,
    sent,
    onDismiss,
}: {
    code: string;
    sent: boolean;
    onDismiss: () => void;
}) {
    const [copied, setCopied] = useState<boolean | null>(null);

    return (
        <Card variant="warn" padding="md" role="region" aria-label={previewCopy.codeTitle}>
            <MonoLabel size={10} tone="warn" as="div" className="mb-2">
                {previewCopy.codeTitle}
            </MonoLabel>
            <output className="block font-[family-name:var(--font-mono)] text-[24px] leading-[1.3] tracking-[0.12em] text-[var(--warn-ink)] select-all">
                {code}
            </output>
            <p className="m-0 mt-2 text-[13px] leading-[1.5] text-[var(--warn-ink)]">
                {sent ? previewCopy.codeWarningSent : previewCopy.codeWarning}
            </p>
            {copied === false ? (
                <p
                    role="alert"
                    className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--warn-ink)]"
                >
                    {previewCopy.copyFailed}
                </p>
            ) : null}
            <div className="mt-3 flex flex-wrap gap-2">
                <Button
                    variant="outline"
                    size="md"
                    onClick={async () => setCopied(await copyText(code))}
                >
                    {copied ? previewCopy.codeCopied : previewCopy.copyCode}
                </Button>
                <Button variant="outline" size="md" onClick={onDismiss}>
                    {previewCopy.codeClose}
                </Button>
            </div>
        </Card>
    );
}

function Figure({ value, caption }: { value: string; caption: string }) {
    return (
        <div>
            <div className="font-[family-name:var(--font-display)] text-[20px] leading-[1.2] font-semibold text-[var(--text-on-dark)]">
                {value}
            </div>
            <div className="mt-[2px] text-[11px] leading-[1.3] text-[var(--text-on-dark-4-safe)]">
                {caption}
            </div>
        </div>
    );
}

function rowsFor(
    statistics: ReportLinkStatistics,
    linkState: ReportLinkStatistics['linkState'],
): Array<[string, string]> {
    const sent = statistics.sentAt
        ? [formatDayTimeShort(statistics.sentAt), statistics.sentVia].filter(Boolean).join(' · ')
        : previewCopy.notSent;

    const opened =
        statistics.openedCount === 0
            ? previewCopy.notOpened
            : statistics.openedLastAt
              ? previewCopy.openedTimes(
                    statistics.openedCount,
                    formatDayTimeShort(statistics.openedLastAt),
                )
              : previewCopy.times(statistics.openedCount);

    const validUntil =
        linkState === 'revoked'
            ? previewCopy.linkRevoked
            : statistics.validUntil === null
              ? previewCopy.noLink
              : linkState === 'expired'
                ? previewCopy.linkExpired(formatDayLong(statistics.validUntil))
                : formatDayLong(statistics.validUntil);

    return [
        [previewCopy.rowSent, sent],
        [previewCopy.rowOpened, opened],
        [previewCopy.rowDownloaded, previewCopy.times(statistics.downloadedCount)],
        [previewCopy.rowForwarded, previewCopy.times(statistics.forwardedCount)],
        [previewCopy.rowQuestions, previewCopy.times(statistics.questionsCount)],
        [previewCopy.rowValidUntil, validUntil],
    ];
}
